// PackingQuantity — renders a packing-material quantity in BUNDLES/CASES (BD, CS)
// with an info affordance whose tooltip gives the exact piece count.
//
// Shared by Inventory → Packing Materials and Settings → Packing Materials so the two
// can never render the same number differently.
//
// ⚠️ This component does NOT convert anything. Pieces → bundles is computed server-side
// by /settings/packing-thresholds (FLOOR, using @tnb/packing-consumption's
// PIECES_PER_BUNDLE_SQL). A null `bundles` means the item's pieces-per-bundle could not
// be resolved; we then show raw pieces rather than invent a bundle count.

import React from 'react';
import { Info } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import HoverTooltip from './HoverTooltip';

type Num = number | string | null | undefined;

/** Trailing zeros dropped: 4.00 → "4", 4.50 → "4.5", 8988 → "8,988". */
const formatQty = (value: Num): string => {
  const n = Number(value ?? 0);
  if (!isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

interface UnitsHintProps {
  /** Exact piece count for the tooltip. Null hides the hint entirely. */
  pieces: Num;
}

/**
 * Standalone "ⓘ N u" hint — for placing next to an editable input that is already
 * denominated in bundles (the Safe/Reorder fields in Settings).
 */
export const UnitsHint: React.FC<UnitsHintProps> = ({ pieces }) => {
  const { t } = useLanguage();
  if (pieces === null || pieces === undefined) return null;
  const label = `${formatQty(pieces)} ${t('admin.packingMaterials.unitsAbbrev')}`;
  return (
    <HoverTooltip label={label} className="text-secondary-400 dark:text-gray-500 cursor-help">
      <Info className="w-4 h-4" />
    </HoverTooltip>
  );
};

interface PackingQuantityProps {
  /** Value already converted to bundles/cases by the backend. Null = unresolvable. */
  bundles: Num;
  /** Exact piece count, for the tooltip and for the fallback rendering. */
  pieces: Num;
  /** Packing unit label from ics.inventory.item_unit, e.g. 'BD' or 'CS'. */
  unit?: string | null;
}

/**
 * Read-only quantity: "8 CS ⓘ" with the tooltip "8,988 u".
 * Falls back to "8,988 u" when the bundle conversion is unavailable.
 */
export const PackingQuantity: React.FC<PackingQuantityProps> = ({ bundles, pieces, unit }) => {
  const { t } = useLanguage();
  const unitsAbbrev = t('admin.packingMaterials.unitsAbbrev');

  // No resolvable pieces-per-bundle — show the raw piece count, never a made-up bundle count.
  if (bundles === null || bundles === undefined || !unit) {
    return (
      <span className="inline-flex items-center gap-1">
        <span>{formatQty(pieces)}</span>
        <span className="text-xs font-medium text-secondary-400 dark:text-gray-500">{unitsAbbrev}</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <span>{formatQty(bundles)}</span>
      <span className="text-xs font-medium text-secondary-400 dark:text-gray-500">{unit}</span>
      <UnitsHint pieces={pieces} />
    </span>
  );
};

export default PackingQuantity;
